/**
 * Centralized pricing plan configuration
 * Single source of truth for all plan details across the application
 */

// Base plan configurations
export const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Perfect for testing and small projects',
    color: 'gray',
    popular: false,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'For production apps and teams',
    color: 'blue',
    popular: true,
  }
}

// Pricing configuration
export const PRICING = {
  free: {
    monthly: { price: 0, display: '$0' },
    yearly: { price: 0, display: '$0' }
  },
  pro: {
    monthly: { 
      price: 5, 
      display: '$5',
      originalPrice: '$10',
      period: '/month'
    },
    yearly: { 
      price: 36, 
      display: '$36',
      originalPrice: '$120',
      period: '/year',
      periodNote: '$3/month · Save $24 per year'
    }
  }
}

// Plan features - used across pricing pages, billing, and auth
export const PLAN_FEATURES = {
  free: [
    '10 API calls per month',
    'Max 200KB image upload',
    'PNG & WebP formats',
    'Standard quality icons',
    'Basic email support'
  ],
  pro: [
    '10,000 API calls per month',
    'Max 5MB image upload',
    'All formats supported',
    'High quality icons',
    'Priority email support',
    'Custom badges & effects',
    'Priority processing',
    'Advanced customization'
  ]
}

// Feature comparison for detailed pricing tables
export const COMPARISON_FEATURES = [
  { 
    category: 'Usage', 
    features: [
      { name: 'API Calls', free: '10/month', pro: '10,000/month' },
      { name: 'Max Image Size', free: '200KB', pro: '5MB' },
      { name: 'Rate Limit', free: '10 req/min', pro: '100 req/min' },
    ]
  },
  { 
    category: 'Formats', 
    features: [
      { name: 'PNG Output', free: true, pro: true },
      { name: 'WebP Output', free: true, pro: true },
    ]
  },
  { 
    category: 'Features', 
    features: [
      { name: 'iOS Icons', free: true, pro: true },
      { name: 'Android Icons', free: true, pro: true },
      { name: 'Custom Badges', free: false, pro: true },
    ]
  },
  { 
    category: 'Support', 
    features: [
      { name: 'Email Support', free: 'Basic', pro: 'Priority' },
    ]
  },
]

// Billing tab specific details
export const BILLING_PLAN_DETAILS = {
  free: {
    name: 'Free Plan',
    price: 0,
    description: PLANS.free.description,
    features: PLAN_FEATURES.free
  },
  pro: {
    name: 'Pro Plan',
    price: 36,
    description: PLANS.pro.description,
    features: PLAN_FEATURES.pro
  }
}

/**
 * Get formatted plan data for pricing displays
 * @param {boolean} isYearly - Whether to show yearly pricing
 * @returns {Array} Array of plan objects with pricing info
 */
export const getPricingPlans = (isYearly = true) => {
  const billingPeriod = isYearly ? 'yearly' : 'monthly'
  
  return [
    {
      ...PLANS.free,
      price: PRICING.free[billingPeriod].display,
      period: '/month',
      features: PLAN_FEATURES.free,
      cta: 'Get Started Free'
    },
    {
      ...PLANS.pro,
      price: PRICING.pro[billingPeriod].display,
      originalPrice: PRICING.pro[billingPeriod].originalPrice,
      period: PRICING.pro[billingPeriod].period,
      periodNote: PRICING.pro[billingPeriod].periodNote || '',
      features: PLAN_FEATURES.pro,
      cta: 'Upgrade to Pro',
      onSale: true
    }
  ]
}

/**
 * Get plan details by plan type
 * @param {string} planType - 'free' or 'pro'
 * @returns {Object} Plan details object
 */
export const getPlanDetails = (planType) => {
  const type = planType?.toLowerCase() === 'pro' ? 'pro' : 'free'
  return BILLING_PLAN_DETAILS[type]
}

/**
 * Check if a plan is the Pro plan
 * @param {Object|string} plan - Plan object or plan name
 * @returns {boolean}
 */
export const isPro = (plan) => {
  const planName = typeof plan === 'string' ? plan : plan?.name
  return planName?.toLowerCase() === 'pro'
}
